import classNames from "classnames";
import React from "react";
import { InputBaseProps } from "..";
import { SanityInputCheckboxesProps } from "../../../models/form/SanityFormInputField";
import { isNullOrWhiteSpace } from "../../../shared/string";
import ControlledCheckbox from "../../ControlledInputs/ControlledCheckbox";
import { Inputs } from "../Inputs";
import { useFieldValidator } from "../Validation";
import { renderHiddenOptionValues } from "./DynamicInputs";
import InputContainer from "./InputContainer";
import { useFieldCustom } from "./useFieldCustom";

export type CheckboxesProps = InputBaseProps<SanityInputCheckboxesProps>;

const Checkboxes = ({
  inputContainerVariant,
  name,
  label,
  options,
  readonly,
  validation
}: CheckboxesProps) => {
  const validator = useFieldValidator(validation);

  const [field, meta, helpers] = useFieldCustom<string[]>({
    name,
    validate: validator
  });

  const onValueSelected: React.InputHTMLAttributes<HTMLInputElement>["onChange"] = e => {
    const fieldValues = [...field.value];
    const newValue = e.currentTarget.value;

    if (!fieldValues.includes(newValue)) {
      fieldValues.push(e.currentTarget.value);
    } else {
      const index = fieldValues.indexOf(newValue);
      fieldValues.splice(index, 1);
    }

    helpers.setTouched(true);
    helpers.setValue(fieldValues);
  };

  const error = meta.touched && !!meta.error;

  const optionsWithSubfields = options.filter(option => option.inputs?.length);
  const selectedOptionsWithSubfields = optionsWithSubfields.filter(option =>
    meta.value?.includes(option.value)
  );
  const notSelectedOptionsWithSubfields = optionsWithSubfields.filter(
    option => meta.value?.includes(option.value) !== true
  );

  return (
    <>
      <InputContainer meta={meta} variant={inputContainerVariant}>
        {!isNullOrWhiteSpace(label) && (
          <label
            className={classNames(
              "block",
              inputContainerVariant === "sectioned"
                ? "mb-4 text-brand-primary-20 text-lg lg:text-2xl"
                : "mb-2 text-neutral-40 font-semibold"
            )}
            htmlFor={name}
          >
            {label}
          </label>
        )}
        <div className="flex flex-col gap-4">
          {options?.map((option, index) => {
            const checked = meta.value?.includes(option.value);

            return (
              <ControlledCheckbox
                additionalClassName="mr-8"
                checked={checked}
                error={error}
                key={index}
                label={option.label ?? option.value}
                name={name}
                onChange={onValueSelected}
                readonly={readonly}
                value={option.value}
              />
            );
          })}
        </div>
      </InputContainer>
      {!!selectedOptionsWithSubfields.length && (
        <div className="border-l border-input-border pl-4">
          {selectedOptionsWithSubfields.map((option, index) => (
            <Inputs
              inputs={option.inputs ?? []}
              key={index}
              readonly={readonly ?? false}
            />
          ))}
        </div>
      )}
      {!!notSelectedOptionsWithSubfields.length &&
        renderHiddenOptionValues(notSelectedOptionsWithSubfields)}
    </>
  );
};

export default Checkboxes;

